/* eslint-disable */
// /src/components/BottomNav.jsx
import React, { useRef, useState, useEffect } from "react";
import styled from "styled-components";
import { useDriverStatusContext } from "context/DriverStatusContext";
import { useAppConfig } from "context/AppConfigContext";
import { subscribeMyUnreadCount } from "../services/notificationsService";

/** props
 * - active: "home" | "faq" | "my"
 * - onChange?: (key) => void
 * - navigate?: (path) => void
 */
export default function BottomNav({ active = "home", onChange, navigate }) {
    const { status, phoneE164, loading } = useDriverStatusContext();
    const { config } = useAppConfig();
    const accessCfg = config.access || {};
    const guardFaq = accessCfg.faq || {};

    const isLoggedIn = !!phoneE164;
    const isMember = status === "member";
    const isReady = loading === false;

    // 로딩 안내 알럿이 과하게 뜨지 않도록 1회만
    const shownLoadingAlertRef = useRef(false);


    const [unreadCount, setUnreadCount] = useState(0);

    useEffect(() => {
        // 로그인 상태에서만 구독(배지용 onSnapshot 허용 범위)
        if (!isLoggedIn) {
            setUnreadCount(0);
            return;
        }

        const unsub = subscribeMyUnreadCount((n) => {
            setUnreadCount(Number(n || 0));
        });

        return () => {
            try {
                unsub && unsub();
            } catch { }
        };
    }, [isLoggedIn]);


    // 🔍 컴포넌트 렌더될 때 상태 한번 찍기
    console.log("[BottomNav] render", {
        active,
        phoneE164,
        status,
        loading,
        isLoggedIn,
        isMember,
        guardFaq,
    });

    const items = [
        { key: "home", label: "홈", path: "/home", Icon: IconHome },
        { key: "notifications", label: "알림", path: "/notifications", Icon: IconBell },
        { key: "faq", label: "FAQ", path: "/faq", Icon: IconHeadset },
        { key: "my", label: "내정보", path: "/my", Icon: IconUser },
    ];

    const notifyLoadingOnce = () => {
        if (shownLoadingAlertRef.current) return;
        shownLoadingAlertRef.current = true;
        window.alert("불러오는 중입니다. 잠시 후 다시 시도해 주세요.");
        setTimeout(() => {
            shownLoadingAlertRef.current = false;
        }, 1200);
    };

    const go = (it) => {
        onChange?.(it.key);
        if (!navigate) return;

        console.log("[BottomNav] go() called", {
            tabKey: it.key,
            path: it.path,
            isLoggedIn,
            isMember,
            isReady,
            loading,
            guardFaq,
        });

        try {
            // ✅ 공통: 상태 확인 중(loading=true)에는 가드 판단 보류
            // - 로그인 직후 phoneE164/status가 아직 세팅되기 전 "첫 클릭 튕김" 방지
            if (!isReady) {
                console.log("[BottomNav] block: not ready (loading)");
                notifyLoadingOnce();
                return;
            }

            // 1) 내정보: 로그인 필요 (ready 이후에만 판단)
            if (it.key === "my" && !isLoggedIn) {
                console.log("[BottomNav] block MY: not logged in (ready=false handled above)");
                window.alert("로그인 후 이용 가능합니다.");
                navigate("/login");
                return;
            }

            // 1-1) 알림: 로그인 필요
            if (it.key === "notifications" && !isLoggedIn) {
                window.alert("로그인 후 이용 가능합니다.");
                navigate("/login");
                return;
            }

            // 2) FAQ: access.faq.enabled === true 일 때만 가드 적용 (ready 이후에만 판단)
            if (it.key === "faq") {
                console.log("[BottomNav] FAQ tab clicked", {
                    guardEnabled: !!guardFaq.enabled,
                });

                if (guardFaq.enabled) {
                    if (!isMember) {
                        const msg =
                            guardFaq.message ||
                            "헤이웨이 가맹 기사 전용 서비스입니다.\n가맹 승인 후 이용 가능합니다.";
                        console.log("[BottomNav] block FAQ: not member, show message:", msg);
                        window.alert(msg);
                        return;
                    } else {
                        console.log("[BottomNav] allow FAQ: member && guard.enabled=true");
                    }
                } else {
                    console.log("[BottomNav] allow FAQ: guard.enabled=false (open to all)");
                }
            }
        } catch (e) {
            console.warn("[BottomNav] guard error", e);
        }

        console.log("[BottomNav] navigate to", it.path);
        navigate(it.path);
    };

    return (
        <Bar role="navigation" aria-label="하단 탭바">
            <Inner>
                {items.map((it) => {
                    const isActive = active === it.key;
                    return (
                        <Item
                            key={it.key}
                            $active={isActive}
                            onClick={() => go(it)}
                            aria-current={isActive ? "page" : undefined}
                        >
                            <IconWrap>
                                <it.Icon active={isActive} />
                                {it.key === "notifications" && unreadCount > 0 ? (
                                    <Badge aria-label={`미확인 알림 ${unreadCount}개`}>
                                        {unreadCount > 99 ? "99+" : unreadCount}
                                    </Badge>
                                ) : null}
                            </IconWrap>
                            <span className="label">{it.label}</span>
                        </Item>

                    );
                })}
            </Inner>
            <SafePad />
        </Bar>
    );
}

/* ====== styles ====== */
const Bar = styled.nav`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  background: #ffffffcc;
  backdrop-filter: saturate(120%) blur(8px);
  box-shadow: 0 -6px 24px rgba(0, 0, 0, 0.06);
  z-index: 50;
`;

const Inner = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  max-width: 520px;
  margin: 0 auto;
  padding: 10px 12px 10px 12px;
`;

const Item = styled.button`
  appearance: none;
  border: 0;
  background: transparent;
  cursor: pointer;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;

  .label {
    font-size: 11px;
    font-weight: ${({ $active }) => ($active ? 700 : 500)};
    line-height: 1;
    letter-spacing: -0.2px;
    color: ${({ $active }) =>
        $active ? "var(--color-primary, #D9A679)" : "#aaa"};
  }

  &:active {
    transform: translateY(1px);
  }
`;

const IconWrap = styled.div`
  position: relative;
  width: 24px;
  height: 24px;
  display: grid;
  place-items: center;
`;

const Badge = styled.span`
  position: absolute;
  top: -6px;
  right: -10px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: #ef4444;
  color: #fff;
  font-size: 11px;
  line-height: 18px;
  text-align: center;
  font-weight: 700;
  box-shadow: none;
`;


const SafePad = styled.div`
  height: max(8px, env(safe-area-inset-bottom));
`;

/* ====== icons ====== */
function IconHome({ active }) {
    const stroke = active ? "var(--color-primary, #D9A679)" : "#9AA0A6";
    return (
        <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke={stroke}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M3 11.5L12 4l9 7.5" />
            <path d="M5 10.5V20a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1v-9.5" />
        </svg>
    );
}

function IconBell({ active }) {
    const stroke = active ? "var(--color-primary, #D9A679)" : "#9AA0A6";
    return (
        <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke={stroke}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M18 8a6 6 0 10-12 0c0 7-3 7-3 7h18s-3 0-3-7" />
            <path d="M13.7 21a2 2 0 01-3.4 0" />
        </svg>
    );
}

function IconGift({ active }) {
    const stroke = active ? "var(--color-primary, #D9A679)" : "#9AA0A6";
    return (
        <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke={stroke}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <rect x="3" y="8" width="18" height="13" rx="2" />
            <path d="M12 8v13" />
            <path d="M3 12h18" />
            <path d="M12 8c-1.5 0-4-1-4-3s2.5-2 4 0c1.5-2 4-1 4 1s-2.5 2-4 2z" />
        </svg>
    );
}

function IconHeadset({ active }) {
    const stroke = active ? "var(--color-primary, #D9A679)" : "#9AA0A6";
    const fill = active ? "rgba(217, 166, 121, 0.12)" : "none";

    return (
        <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke={stroke}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path
                d="M4 4h11a4 4 0 0 1 4 4v4a7 7 0 0 1-7 7h-2.2L8 21.5V19H7a4 4 0 0 1-4-4V8a4 4 0 0 1 4-4z"
                fill={fill}
            />
        </svg>
    );
}

function IconUser({ active }) {
    const stroke = active ? "var(--color-primary, #D9A679)" : "#9AA0A6";
    return (
        <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke={stroke}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <circle cx="12" cy="8" r="4" />
            <path d="M4 20c0-3.3 3.6-6 8-6s8 2.7 8 6" />
        </svg>
    );
}
